import { EntityType } from '@/models/EntityTypes';
import { ExtensionTypeIcon } from '@/models/ExtensionTypeIcon';
import { Person } from '@/models/Person';
import { Status } from '@/models/Status';
import { differenceInCalendarDays, differenceInWeeks, format, formatDistanceStrict } from 'date-fns';
import { enUS, es, ptBR } from 'date-fns/locale';
import moment from 'moment-timezone';
import Slugify from 'react-slugify';
import _ from 'underscore';
import Constants from '../Constants';
import { LanguageType } from '../models/LanguageType';
import { store } from '../store';
import { LocaleData } from '../translations/LocaleData';

interface LanguageDateFormat {
    language: LanguageType;
    format: string;
    formatDayAndMonth: string;
}

const formatDate = (
    date: Date,
    language: LanguageType,
    onlyDayAndMonth: boolean = false,
    withTime: boolean = false,
    separator: string = '/',
): string => {
    const formats: LanguageDateFormat[] = [
        {
            language: 'pt',
            format: `dd${separator}MM${separator}yyyy${withTime ? ' HH:mm' : ''}`,
            formatDayAndMonth: `dd${separator}MM`,
        },
        {
            language: 'en',
            format: `MM${separator}dd${separator}yyyy${withTime ? ' HH:mm' : ''}`,
            formatDayAndMonth: `MM${separator}dd`,
        },
    ];

    const currentFormat = formats.find((languageDateFormat: LanguageDateFormat) => languageDateFormat.language === language) || formats[0];
    return onlyDayAndMonth ? format(date, currentFormat.formatDayAndMonth) : format(date, currentFormat.format);
};

const getFormattedExpirationDate = (finishDate: Date | undefined, daysToExpire: number, expired: boolean, isCourse = true) => {
    let result;
    if (finishDate) {
        const strings: LocaleData = store.getState().locale.data;
        if (!expired) {
            if (daysToExpire <= 30 && daysToExpire > 1) {
                result = `${strings.EXPIRES_IN} ${daysToExpire} ${strings.DAYS.toLocaleLowerCase()}`;
            } else if (daysToExpire == 1) {
                result = `${strings.EXPIRES_IN} ${daysToExpire} ${strings.DAY.toLocaleLowerCase()}`;
            } else if (daysToExpire == 0) {
                result = `${strings.EXPIRES} ${strings.TODAY.toLocaleLowerCase()}`;
            } else {
                result = `${strings.UNTIL} ${formatDate(finishDate, strings._LANGUAGE as LanguageType)}`;
            }
        } else {
            result = isCourse ? strings.COURSE_EXPIRED : strings.PROGRAM_EXPIRED;
        }
    }

    return result;
};

const isExpireDateViewByStatus = (status: Status | undefined): boolean => {
    const completedStatusList: Status[] = [
        Constants.courseEnrollmentStatus.COMPLETED,
        Constants.contentEnrollmentStatus.FAILED,
        Constants.courseEnrollmentStatus.PASSED,
    ];

    return !_.contains(completedStatusList, status);
};

const getElapsedTime = (locale: string, date: Date): string => {
    return formatDistanceStrict(date, new Date(), {
        locale: resolveLocale(locale),
        addSuffix: true,
        roundingMethod: 'round',
    });
};

const getFormatDateInHoursAndMinutes = (date: Date) => {
    return format(date, 'HH:mm');
};

const resolveLocale = (locale: string) => {
    switch (locale) {
        case Constants.language.EN:
            return enUS;
        case Constants.language.PT:
            return ptBR;
        default:
            return es;
    }
};

const formatToTwoDigits = (value: number, verifyZero: boolean = false) => (value === 0 && verifyZero ? '0' : value.toString().padStart(2, '0'));

const getFormatTime = (timeAsSecond: number): string => {
    const minutes: number = Math.floor(timeAsSecond / 60);
    const seconds: number = Math.floor(timeAsSecond % 60);
    return `${formatToTwoDigits(minutes)}:${formatToTwoDigits(seconds)}`;
};

const getFormatTimeHourMin = (timeAsMinutes: number): string => {
    const hours = Math.floor(timeAsMinutes / 60);
    const minutes = Math.floor(timeAsMinutes % 60);
    const hoursTime = hours > 0 ? `${hours}h ` : '';
    const minustesTime = hours > 0 ? `${formatToTwoDigits(minutes)}min` : `${formatToTwoDigits(minutes, true)} min`;
    return `${hoursTime}${minustesTime}`;
};

/**
 * Retorna a data atual no formato especificado e a diferença de dias ate a data atual
 *
 * @param date data passada
 * @param dateFormat formato da data
 * @param locale locale(_LANGUAGE) atual do sistema
 */
const getFormattedDateAndDistance = (date: Date, dateFormat: string, locale: string): string => {
    const strings: LocaleData = store.getState().locale.data;
    let resultDate: string = '';
    const formattedDate: string = format(date, dateFormat, { locale: resolveLocale(locale) });
    const diffInDays = differenceInCalendarDays(Date.now(), date);
    if (diffInDays < 14) {
        resultDate = `${formattedDate}, ${getElapsedTime(locale, date)}`;
    } else {
        resultDate = `${formattedDate}, ${differenceInWeeks(Date.now(), date)} ${strings.WEEKS}`;
    }
    return resultDate;
};

const isNull = (obj: unknown) => obj === null || obj === undefined;

const shuffle = <T>(arrayToShuffle: Array<T>): Array<T> => {
    const array = [...arrayToShuffle];
    let currentIndex = array.length,
        temporaryValue,
        randomIndex;

    while (0 !== currentIndex) {
        randomIndex = Math.floor(Math.random() * currentIndex);
        currentIndex -= 1;

        temporaryValue = array[currentIndex];
        array[currentIndex] = array[randomIndex];
        array[randomIndex] = temporaryValue;
    }
    return array;
};

const getPercentage = (total: number, value: number): number => (value / total) * 100;

const getPluralization = (amount: number, singular: string, plural: string, none?: string) => {
    if (!amount || amount === 0) {
        return none || '';
    } else if (amount === 1) {
        return amount + ' ' + singular;
    } else {
        return amount + ' ' + plural;
    }
};

const startOfDayFromStrDate = (dateAsStr: string): Date | undefined => {
    const values: string[] = dateAsStr.substr(0, 10).split('-');

    if (values.length === 3) {
        return new Date(Number(values[0]), Number(values[1]) - 1, Number(values[2]));
    }

    return undefined;
};

const getFormattedDate = (date: Date, dateFormat: string, locale: string): string => format(date, dateFormat, { locale: resolveLocale(locale) });

const getValidUrl = (path: string): string => {
    if (!Boolean(path)) {
        return '';
    }

    if (path.startsWith('http')) {
        return path;
    }

    return encodeURIComponent(path);
};

const getHost = () => {
    if (process.env.NODE_ENV === 'development') {
        return window.location.origin.split(':3000')[0];
    } else {
        return window.location.origin;
    }
};

const removeSpacesAndSpecialCharacters = (value: string): string => {
    const tmpValues: string[] = value.split('.');
    const tmpExt: string = tmpValues.pop() as string;
    const tmpFileName: string = tmpValues.join('');
    return Slugify(tmpFileName, { delimiter: '_' }) + '.' + tmpExt;
};

const getIconByExtensionType = (filePath: string): ExtensionTypeIcon => {
    const extension: string = filePath.substr(filePath.lastIndexOf('.') + 1).toLowerCase();
    const types: ExtensionTypeIcon[] = [
        { ext: Constants.imageTypesList, icon: Constants.icons.IMAGE_OUTLINE },
        { ext: Constants.audioTypesList, icon: Constants.icons.AUDIOTRACK },
        { ext: Constants.videoTypesList, icon: Constants.icons.ONDEMAND_VIDEO },
        { ext: Constants.docsTypesList, icon: Constants.icons.FILE_DOCUMENT_OUTLINE },
        { ext: Constants.pdfTypeList, icon: Constants.icons.PDF },
    ];
    const genericType: ExtensionTypeIcon = { ext: [], icon: Constants.icons.INSERT_DRIVE_FILE };
    const result: ExtensionTypeIcon = types.find((t: ExtensionTypeIcon) => t.ext.indexOf(extension) !== -1) || genericType;
    return result;
};

const cpfMask = (value: string) => {
    return value
        ?.replace(/\D/g, '')
        .replace(/(\d{3})(\d)/, '$1.$2')
        .replace(/(\d{3})(\d)/, '$1.$2')
        .replace(/(\d{3})(\d{1,2})/, '$1-$2')
        .replace(/(-\d{2})\d+?$/, '$1');
};

const getDisplayName = (person: Person, onlyFirstName: boolean = false): string => {
    let result = '';
    if (person) {
        result = person.displayName || (onlyFirstName ? person.firstName : `${person.firstName} ${person.lastName || ''}`);
    }
    return result;
};

const redirectToStore = (isIOS: boolean): void => {
    window.location.href = isIOS ? Constants.mobileApp.IOS_APP_STORE : Constants.mobileApp.ANDROID_PLAY_STORE;
};

const getHoursList = (startAt: number = 0, endAt: number = 24, sliceAt: number = 15, skipSlice: number = 0) => {
    const result: string[] = [];
    let skipCount = 0;

    for (let i = startAt; i < endAt; i++) {
        const hh: string = addZeroToLeft(i);
        for (let j = 0; j < 60; j += sliceAt) {
            if (skipSlice > 0 && skipCount < skipSlice) {
                skipCount = skipCount + 1;
            } else {
                const mm: string = addZeroToLeft(j);
                result.push(`${hh}:${mm}`);
            }
        }
    }
    return result;
};

const addZeroToLeft = (value: number) => {
    return ('0' + value).slice(-2);
};

const momentWithTimeZone = (
    date: number | Date,
    language: LanguageType,
    onlyDayAndMonth: boolean = false,
    onlyHourAndMinute: boolean = false,
    withTime: boolean = false,
    separator: string = '/',
    timezone: string = Constants.DEFAULT_TIME_ZONE,
) => {
    const formats: LanguageDateFormat[] = [
        {
            language: 'pt',
            format: `DD${separator}MM${separator}YYYY${withTime ? ' HH:mm' : ''}`,
            formatDayAndMonth: `DD${separator}MM`,
        },
        {
            language: 'en',
            format: `MM${separator}DD${separator}YYYY${withTime ? ' HH:mm' : ''}`,
            formatDayAndMonth: `MM${separator}DD`,
        },
    ];
    const currentFormat = formats.find((languageDateFormat: LanguageDateFormat) => languageDateFormat.language === language) || formats[0];

    if (_.isNumber(date)) {
        return moment(date)
            .tz(timezone)
            .format(onlyDayAndMonth ? currentFormat.formatDayAndMonth : onlyHourAndMinute ? 'HH:mm' : currentFormat.format);
    } else {
        return formatDate(date, language, onlyDayAndMonth, withTime, separator);
    }
};

const resetNavigationToSetScreen = (type?: EntityType, organizationId?: number | null | undefined, isAdmin?: boolean, history?: any) => {
    if (type && organizationId === null && !isAdmin && history) {
        switch (type) {
            case 'CONTENT':
                history.push('/contents/playlist/items');
                break;
            case 'LIST':
                history.push('/contents/playlist/lists');
                break;
            case 'COURSE':
                history.push('/contents/lms/courses');
                break;
            case 'PROGRAM':
                history.push('/contents/lms/programs');
                break;
            default:
                break;
        }
    }
};

const getNewDate = (date?: Date | number) => {
    if (_.isNumber(date)) {
        return new Date(momentWithTimeZone(date, 'en', false, false, true));
    } else {
        return date ? new Date(date) : undefined;
    }
};

const currentDate = (timezone: string = Constants.DEFAULT_TIME_ZONE): Date => {
    return new Date(momentWithTimeZone(new Date().getTime(), 'en', false, false, true));
};

const isProd = () => {
    const devPaths: string[] = ['https://dev.', 'https://dev-', 'https://stag-'];
    return devPaths.filter((x: string) => window.location.href.startsWith(x)).length === 0;
};

export {
    getFormatTime,
    getElapsedTime,
    formatDate,
    getFormattedDateAndDistance,
    isNull,
    shuffle,
    getPluralization,
    resolveLocale,
    startOfDayFromStrDate,
    getFormatTimeHourMin,
    getFormattedDate,
    getValidUrl,
    getPercentage,
    getHost,
    removeSpacesAndSpecialCharacters,
    getIconByExtensionType,
    cpfMask,
    getDisplayName,
    redirectToStore,
    getFormattedExpirationDate,
    isExpireDateViewByStatus,
    getHoursList,
    addZeroToLeft,
    resetNavigationToSetScreen,
    getFormatDateInHoursAndMinutes,
    momentWithTimeZone,
    getNewDate,
    currentDate,
    isProd,
};
